import { Injectable } from '@angular/core';
import { TOAST_MESSAGE_LIFETIME_DURATIONS, TOAST_MESSAGE_SEVERITY_LEVELS } from '@app/shared/const';
import { MessageService } from 'primeng/api';
import { TranslationService } from './translation.service';

export interface ToastConfig {
  isTranslate?: boolean;
  params?: any;
  life?: number;
  defaultErrorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(
    private messageService: MessageService,
    private translationService: TranslationService
  ) {}

  addToastMessage(
    severity: string,
    detail: string,
    lifeTime = TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
    isTranslate = true
  ): void {
    if (detail) {
      this.messageService.add({
        severity: severity,
        detail: isTranslate ? this.translationService.getTranslation(detail) : detail,
        life: lifeTime
      });
    }
  }

  addToastMessageApiResponse(
    isSuccess: boolean,
    successDetail: string,
    failureDetail = '',
    lifeTime = TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
    params: any = undefined,
    isTranslate = true
  ): void {
    const severity = isSuccess
      ? TOAST_MESSAGE_SEVERITY_LEVELS.success
      : TOAST_MESSAGE_SEVERITY_LEVELS.error;
    const detail = isSuccess ? successDetail : failureDetail;
    this.addMessageWithTranslateParams(severity, detail, params, lifeTime, isTranslate);
  }

  clearAllToastMessages(key?: string): void {
    this.messageService.clear(key);
  }

  addMessageWithTranslateParams(
    severity: string,
    key: string,
    params: any,
    lifeTime = TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
    isTranslate = true
  ): void {
    let detail = this.translationService.getTranslationWithParams(key, params) ?? '';
    if (detail) {
      detail = detail.charAt(0).toUpperCase() + detail.slice(1);
      this.addToastMessage(severity, detail, lifeTime, isTranslate);
    }
  }

  apiResponse(
    isSuccess: boolean,
    successKey: string,
    failureKey: string,
    config?: ToastConfig
  ): void {
    const severity = isSuccess
      ? TOAST_MESSAGE_SEVERITY_LEVELS.success
      : TOAST_MESSAGE_SEVERITY_LEVELS.error;
    const detail = isSuccess ? successKey : config?.defaultErrorMessage ?? failureKey;
    this.addMessageWithTranslateParams(
      severity,
      detail,
      config?.params,
      config?.life ?? TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
      config?.isTranslate ?? true
    );
  }

  success(key: string, config?: ToastConfig): void {
    this.addMessageWithTranslateParams(
      TOAST_MESSAGE_SEVERITY_LEVELS.success,
      key,
      config?.params,
      config?.life ?? TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
      config?.isTranslate ?? true
    );
  }

  error(key: string, config?: ToastConfig): void {
    this.addMessageWithTranslateParams(
      TOAST_MESSAGE_SEVERITY_LEVELS.error,
      key,
      config?.params,
      config?.life ?? TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
      config?.isTranslate ?? true
    );
  }

  info(key: string, config?: ToastConfig): void {
    this.addMessageWithTranslateParams(
      TOAST_MESSAGE_SEVERITY_LEVELS.info,
      key,
      config?.params,
      config?.life ?? TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
      config?.isTranslate ?? true
    );
  }

  warn(key: string, config?: ToastConfig): void {
    this.addMessageWithTranslateParams(
      TOAST_MESSAGE_SEVERITY_LEVELS.warn,
      key,
      config?.params,
      config?.life ?? TOAST_MESSAGE_LIFETIME_DURATIONS.medium,
      config?.isTranslate ?? true
    );
  }
}
